import { InternalColumns } from 'features/importerv2/importer-types'

// 'duplicate' is a display-only severity (red, like an error, but never excludes
// a row). validateContactRows only ever emits 'error' | 'warning'; the Validate
// grid synthesizes 'duplicate' issues onto email cells for duplicate rows.
export type IssueSeverity = 'error' | 'warning' | 'duplicate'

export interface RowIssue {
  field: string
  message: string
  severity: IssueSeverity
}

const EMAIL_LOGICAL_FIELDS = [
  'contact_work_email',
  'contact_personal_email',
  'contact_generic_email',
] as const
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * TLDs that are valid but uncommon enough to warrant a "verify before import"
 * warning. Both `.co` and `.cm` are frequent `.com` finger-slips (and `.cm` is
 * a known phishing vector), so a well-formed address on either is flagged for
 * the user to eyeball before importing.
 */
const UNUSUAL_TLDS = ['.co', '.cm'] as const

/** Minimum digit count for a phone number to be considered complete. */
const MIN_PHONE_DIGITS = 10

/** Returns true when a populated email ends in one of the unusual TLDs. */
function hasUnusualTld(email: string): boolean {
  const lower = email.toLowerCase()
  return UNUSUAL_TLDS.some((tld) => lower.endsWith(tld))
}

/**
 * Validate a list of contact CSV rows and return a map of row ID → issues.
 *
 * Rows are keyed by their ORIGINAL CSV header strings (PapaParse output).
 * The `headers` array maps each original key → logical mapped field name.
 * Validation reads values via the original key (looked up through
 * `mappedToOriginal`) so it works correctly regardless of CSV column names.
 *
 * Each issue carries a `severity`:
 * - `'error'`   — blocks a clean import; the row is invalid as-is.
 * - `'warning'` — likely fine but worth a human glance before importing.
 *
 * Error rules:
 * - Row must have at least a first name (contact_first_name OR contact_full_name);
 *   last name is optional.
 * - Row must have at least one email across the three email fields.
 * - Each populated email must match a basic format.
 * - Row must have a non-empty company_name.
 *
 * Note: in-file duplicate emails are NOT errors — the backend merges duplicate
 * rows into a single contact. Use `findInFileDuplicateRowIds` to surface them
 * informationally (e.g. in the Duplicates tab).
 *
 * Warning rules (mirror the design mockup's softer validators):
 * - A phone number that is present but has fewer than 10 digits (likely incomplete).
 * - A well-formed email on an unusual TLD (e.g. `.co`) — verify before importing.
 * - A name field that is all-lowercase (likely un-capitalised, worth a glance).
 *
 * `RowIssue.field` is always the LOGICAL field name (e.g. 'contact_work_email'),
 * so the grid can map issues to the right column via the header's mapped field.
 */
export function validateContactRows(
  rows: Record<string, unknown>[],
  headers: { original: string; mapped: string }[]
): Record<string, RowIssue[]> {
  // Build logical → original lookup (only for non-ignored mappings).
  const mappedToOriginal: Record<string, string> = {}
  for (const h of headers) {
    if (h.mapped !== 'ignore') {
      mappedToOriginal[h.mapped] = h.original
    }
  }

  /** Read a logical field value from a row via the original key. */
  const get = (row: Record<string, unknown>, logical: string): string => {
    const orig = mappedToOriginal[logical]
    if (!orig) return ''
    return String(row[orig] ?? '').trim()
  }

  const issues: Record<string, RowIssue[]> = {}

  const add = (
    id: string,
    field: string,
    message: string,
    severity: IssueSeverity
  ) => {
    ;(issues[id] ??= []).push({ field, message, severity })
  }

  // Validate each row.
  for (const r of rows) {
    const id = String(r[InternalColumns.ROW_ID])

    // ── Errors ───────────────────────────────────────────────────────────
    const firstName = get(r, 'contact_first_name')
    const fullName = get(r, 'contact_full_name')
    // A contact needs at least a first name (or a full name). Last name is optional.
    const hasName = fullName !== '' || firstName !== ''
    if (!hasName)
      add(
        id,
        'contact_first_name',
        'First name (or full name) is required',
        'error'
      )

    if (get(r, 'company_name') === '')
      add(id, 'company_name', 'Company name is required', 'error')

    const emails = EMAIL_LOGICAL_FIELDS.map((f) => get(r, f)).filter(Boolean)
    if (emails.length === 0)
      add(id, 'contact_work_email', 'At least one email is required', 'error')

    for (const logicalField of EMAIL_LOGICAL_FIELDS) {
      const v = get(r, logicalField)
      if (!v) continue
      if (!EMAIL_RE.test(v)) {
        add(id, logicalField, `Malformed ${logicalField}`, 'error')
      } else if (hasUnusualTld(v)) {
        // ── Warning: well-formed but unusual TLD ──
        add(
          id,
          logicalField,
          `Unusual email domain — verify "${v}" before importing`,
          'warning'
        )
      }
    }

    // ── Warning: phone present but too short ───────────────────────────────
    const phone = get(r, 'contact_phone')
    if (phone !== '') {
      const digits = phone.replace(/\D/g, '')
      if (digits.length < MIN_PHONE_DIGITS) {
        add(
          id,
          'contact_phone',
          `Phone looks incomplete (${digits.length} of ${MIN_PHONE_DIGITS} digits)`,
          'warning'
        )
      }
    }

    // ── Warning: name field is all-lowercase (likely un-capitalised) ───────
    const nameForCase = fullName !== '' ? fullName : firstName
    if (nameForCase !== '' && nameForCase === nameForCase.toLowerCase()) {
      const field = fullName !== '' ? 'contact_full_name' : 'contact_first_name'
      add(id, field, 'Name is all lowercase — verify capitalisation', 'warning')
    }
  }

  return issues
}

/**
 * Return the set of ROW_IDs whose mapped email appears on more than one row in
 * the file (case-insensitive, across all three email fields).
 *
 * In-file duplicates are NOT errors — the backend merges duplicate rows into a
 * single contact. This helper exists so the UI can surface them informationally
 * (e.g. the Duplicates tab) without excluding them from the import.
 */
export function findInFileDuplicateRowIds(
  rows: Record<string, unknown>[],
  headers: { original: string; mapped: string }[]
): Set<string> {
  const mappedToOriginal: Record<string, string> = {}
  for (const h of headers) {
    if (h.mapped !== 'ignore') {
      mappedToOriginal[h.mapped] = h.original
    }
  }

  const get = (row: Record<string, unknown>, logical: string): string => {
    const orig = mappedToOriginal[logical]
    if (!orig) return ''
    return String(row[orig] ?? '').trim()
  }

  // Collect email → [rowIds] across all rows, keyed by lowercased email.
  const seen = new Map<string, string[]>()
  for (const r of rows) {
    const id = String(r[InternalColumns.ROW_ID])
    for (const logicalField of EMAIL_LOGICAL_FIELDS) {
      const v = get(r, logicalField).toLowerCase()
      if (v) {
        const list = seen.get(v) ?? []
        list.push(id)
        seen.set(v, list)
      }
    }
  }

  const dupes = new Set<string>()
  for (const ids of seen.values()) {
    if (ids.length > 1) {
      for (const id of ids) dupes.add(id)
    }
  }
  return dupes
}
